package com.siriusxm.pia.views.accesscontrols

import androidx.compose.runtime.*
import com.siriusxm.pia.components.Badge
import com.siriusxm.pia.components.BadgeColor
import com.siriusxm.pia.components.expandingIcon
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * Styles for [AccessControlsBadgeView] and its internal rows. Kept as a StyleSheet (rather than
 * inline `style {}` blocks) per this codebase's styling convention.
 *
 * Not installed by this file — [com.siriusxm.cmc.ApplicationStyles] calls `Style(this)` once at
 * app root (and once per embedded app, via the shared `ServiceApplication` base class), the same
 * way every other cmc-ui StyleSheet is registered. Calling `Style()` here instead would re-mount
 * a duplicate `<style>` tag on every render of this view — this component is used per-row in the
 * entity data table, so that would mean one duplicate tag per row.
 *
 * [badgeGroup] is intentionally not shared with `AccountStyles.badgeRow` (a near-identical
 * flex/wrap/gap recipe used by the account/permissions pages) — that style lives in the root
 * app's `src/jsMain`, a module cmc-ui cannot depend on without inverting the dependency graph, so
 * the two recipes are kept independently rather than forcing an inappropriate coupling.
 */
object AccessControlBadgeStyles : StyleSheet() {
    val expandedList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.5.cssRem)
        // A little breathing room above and below the variant rows so the
        // badges don't sit flush against the surrounding row edges.
        padding(0.35.cssRem, 0.px)
    }

    val variantRow by style {
        display(DisplayStyle.Flex)
        gap(0.5.cssRem)
        alignItems(AlignItems.Center)
    }

    val variantLabel by style {
        fontWeight(600)
        fontSize(0.9.cssRem)
        width(140.px)
        flexShrink(0)
    }

    val badgeGroup by style {
        display(DisplayStyle.Flex)
        gap(0.5.cssRem)
        flexWrap(FlexWrap.Wrap)
        alignItems(AlignItems.Center)
    }

    val clickable by style {
        property("cursor", "pointer")
    }
}

/**
 * Renders the access-control badges for an entity, either as a compact single-row summary
 * (an aggregate badge per control, plus an expand chevron when other variants exist) or as a
 * fully expanded list of every variant with its own badge row.
 *
 * This is the standard access-control display used across CMC (aggregator entity header, data
 * table, and any embedded application that pulls in this shared module) so access controls read
 * the same way everywhere in the console. This is read-only.
 *
 * Collapsed behavior: shows one badge per control (Visible/Discoverable/Recommendable), colored
 * by whether every variant agrees on that control — green when all variants enable it, gray with
 * a "✗" when all variants disable it, or yellow with no "✗" when variants disagree (e.g. default
 * disables recommendable but auto360l enables it), signaling there's nuance worth expanding for.
 *
 * Expanded behavior: shows every variant's own label and badges on its own row, unaffected by
 * what other variants contain.
 *
 * @param variants the access control variants to display; if empty, nothing renders.
 * @param alwaysExpanded when true, always renders the full per-variant list with no collapse
 *   affordance (data table view); when false, starts collapsed and toggles via chevron
 *   (compact header view).
 * @param key identifies which entity this view is showing, so the collapsed/expanded state
 *   resets when the caller reuses this composition slot for a different entity. Defaults to
 *   [variants] itself, which resets correctly in the common case but can't distinguish two
 *   different entities that happen to have content-identical access controls — pass the
 *   entity's own id when one is available (as the compact header view does) for a fully
 *   reliable reset.
 */
@Composable
fun AccessControlsBadgeView(
    variants: List<AccessControlVariant>,
    alwaysExpanded: Boolean = false,
    key: Any = variants,
) {
    if (variants.isEmpty()) return

    val hasVariants = variants.size > 1
    // Keyed on both the caller's identity and the variants content: `key` resets state when a
    // different entity reuses this composition slot, and `variants` resets it when the same
    // entity's access controls change in place (e.g. after a reload) without the key changing.
    var expanded by remember(key, variants) { mutableStateOf(false) }

    if (alwaysExpanded || (expanded && hasVariants)) {
        Div({
            classes(AccessControlBadgeStyles.expandedList)
        }) {
            variants.forEach { variant ->
                AccessControlVariantRow(label = variant.displayLabel, state = variant.state)
            }
            if (!alwaysExpanded) {
                expandingIcon(expanded = true) {
                    title = "Collapse variants"
                    onToggle { expanded = false }
                }
            }
        }
    } else {
        Div({
            classes(AccessControlBadgeStyles.badgeGroup)
            if (hasVariants) classes(AccessControlBadgeStyles.clickable)
            if (hasVariants) {
                onClick { expanded = true }
            }
        }) {
            variants.agreementsByField().forEach { (field, agreement) ->
                ControlBadge(field.label, agreement)
            }
            if (hasVariants) {
                expandingIcon(expanded = false) {
                    title = "Expand variants"
                }
            }
        }
    }
}

/**
 * A single labeled row within the expanded view: the variant's display label on the left and
 * its three access-control badges on the right.
 */
@Composable
private fun AccessControlVariantRow(label: String, state: AccessControlState) {
    Div({ classes(AccessControlBadgeStyles.variantRow) }) {
        Div({ classes(AccessControlBadgeStyles.variantLabel) }) {
            Text(label)
        }
        Div({ classes(AccessControlBadgeStyles.badgeGroup) }) {
            ACCESS_CONTROL_FIELDS.forEach { field ->
                ControlBadge(field.label, listOf(field.select(state)).toControlAgreement())
            }
        }
    }
}

/**
 * Renders a single access-control badge for the given [agreement]: green with the plain label
 * for [ControlAgreement.ALL_ENABLED], gray and "✗"-prefixed for [ControlAgreement.ALL_DISABLED],
 * or yellow with the plain label (no "✗") for [ControlAgreement.MIXED] — a disagreement isn't
 * simply "off", so it isn't given the disabled prefix.
 *
 * Used both for a single variant's state (converted to ALL_ENABLED/ALL_DISABLED by the caller)
 * and for the cross-variant aggregate, so the label/color mapping lives in exactly one place.
 */
@Composable
private fun ControlBadge(label: String, agreement: ControlAgreement) {
    val (text, color) = when (agreement) {
        ControlAgreement.ALL_ENABLED -> label to BadgeColor.GREEN
        ControlAgreement.ALL_DISABLED -> "✗ $label" to BadgeColor.GRAY
        ControlAgreement.MIXED -> label to BadgeColor.YELLOW
    }
    Badge(text = text, color = color)
}
